import { useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InputGroup } from '@blueprintjs/core';
import type { ColDef } from 'ag-grid-community';
import { toast } from 'sonner';
import WorkspaceGrid from '../WorkspaceGrid/WorkspaceGrid';
import MultiSelect from '../MultiSelect';
import { fetchBatches, fetchLeadCount, fetchLeadSources, fetchLeads, promoteBulk, type BatchLead, type BatchSummary, type LeadFilter } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { useTableZoom } from '../../utils/tableViewSettings';
import { GridZoomControl } from '../ui/GridZoomControl';
import { ErrorNote, LEAD_IMPORT_ZOOM, Pill, REASON_LABEL, Segmented, VerifyCell, fmtDay, fmtInt, useConfirm } from './ui';

/** Page sizes (spec v4 A4). "All" loads in chunks of the server's 1,000-row maximum as the grid scrolls. */
type PageSize = '50' | '200' | '500' | 'all';
const ALL_CHUNK = 1000;
/** Grid column -> server sort key. Anything else sorts by where the lead came from. */
const SORTABLE: Record<string, string> = {
  origin_row: 'origin', origin: 'origin', business_name: 'business_name', name: 'last_name', phone: 'phone',
  email: 'email', state: 'state', batch_date: 'batch_date',
};

/** Grid column for a server sort key; the inverse of SORTABLE. */
const columnFor = (sortKey: string, across: boolean) =>
  sortKey === 'last_name' ? 'name' : sortKey === 'origin' ? (across ? 'origin' : 'origin_row') : sortKey;

/** What the browser shows right now; handed to the parent's actions. */
export interface LeadsView {
  filter: LeadFilter;
  sortKey: string;
  desc: boolean;
  selected: BatchLead[];
  selectedIds: number[];
  total: number;
  /** total is a floor: the view matches more leads than were counted. */
  totalIsLowerBound: boolean;
  /** Every lead the filter matches is chosen, not only the loaded rows that are checked. */
  allMatching: boolean;
  clearSelection: () => void;
}

type Assignment = NonNullable<LeadFilter['assignment']>;
type Repeat = NonNullable<LeadFilter['repeat']>;
type Eligibility = NonNullable<LeadFilter['eligibility']>;

/**
 * The leads grid used by an import's workspace (batchId set) and by the
 * cross-import views (batchId null, with import, source and batch-date
 * filters). The same filter drives the grid and any First-N or Random-N draw,
 * so what is shown is what gets assigned [F03].
 */
export default function LeadsBrowser({
  batchId = null, summary, showChecks, initial, initialSort, onOpenLead, height = 'calc(100vh - 380px)',
  createMerchants = true, renderActions, renderAside, renderFooter,
}: {
  batchId?: number | null;
  /** Counts shown on the filter buttons of a single import. */
  summary?: BatchSummary;
  /** Force the phone and email check columns on or off; by default they show once any lead has a result. */
  showChecks?: boolean;
  initial?: LeadFilter;
  initialSort?: { sortKey: string; desc: boolean };
  onOpenLead: (id: number) => void;
  height?: string;
  createMerchants?: boolean;
  renderActions?: (v: LeadsView) => ReactNode;
  renderAside?: (v: LeadsView) => ReactNode;
  renderFooter?: (v: LeadsView) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const across = batchId == null;
  const [batches, setBatches] = useState<number[]>(initial?.batches ?? []);
  const [sources, setSources] = useState<number[]>(initial?.sources ?? []);
  const [sourcesExclude, setSourcesExclude] = useState(initial?.sources_exclude ?? false);
  const [dateFrom, setDateFrom] = useState(initial?.date_from ?? '');
  const [dateTo, setDateTo] = useState(initial?.date_to ?? '');
  const [assignment, setAssignment] = useState<Assignment>(initial?.assignment ?? 'all');
  const [repeat, setRepeat] = useState<Repeat>(initial?.repeat ?? 'all');
  const [eligibility, setEligibility] = useState<Eligibility>(initial?.eligibility ?? 'all');
  const [search, setSearch] = useState(initial?.search ?? '');
  const [appliedSearch, setAppliedSearch] = useState(initial?.search ?? '');
  const defaultSort: { colId: string; sort: 'asc' | 'desc' } = across ? { colId: 'origin', sort: 'desc' } : { colId: 'origin_row', sort: 'asc' };
  const [sort, setSort] = useState(initialSort ? { colId: columnFor(initialSort.sortKey, across), sort: initialSort.desc ? 'desc' as const : 'asc' as const } : defaultSort);
  const [selected, setSelected] = useState<BatchLead[]>([]);
  const [resetKey, setResetKey] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>('200');
  const [page, setPage] = useState(0);
  // The filter "select all matching" was chosen for; any other filter drops it.
  const [allMatchingFor, setAllMatchingFor] = useState<string | null>(null);
  const clearSelection = () => { setAllMatchingFor(null); setResetKey((k) => k + 1); };
  const onSelectionChanged = (next: BatchLead[]) => {
    if (next.length < selected.length) setAllMatchingFor(null);
    setSelected(next);
  };

  const filter: LeadFilter = useMemo(() => ({
    ...(batchId == null ? {
      ...(batches.length ? { batches } : {}),
      ...(sources.length ? { sources, ...(sourcesExclude ? { sources_exclude: true } : {}) } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    } : { batch: batchId }),
    assignment, repeat, eligibility,
    ...(appliedSearch ? { search: appliedSearch } : {}),
  }), [batchId, batches, sources, sourcesExclude, dateFrom, dateTo, assignment, repeat, eligibility, appliedSearch]);
  const filterKey = JSON.stringify(filter);
  const sortKey = SORTABLE[sort.colId] ?? 'origin';
  const desc = sort.sort === 'desc';
  const paged = pageSize !== 'all';
  const size = paged ? Number(pageSize) : ALL_CHUNK;
  // A new filter, order or page size starts again on the first page.
  const [pageFor, setPageFor] = useState('');
  const pageReset = `${filterKey}|${sortKey}|${desc}|${pageSize}`;
  if (pageFor !== pageReset) { setPageFor(pageReset); setPage(0); }

  const committed = useQuery({
    queryKey: leadImportKeys.batches({ status: 'Committed' }), queryFn: () => fetchBatches({ status: 'Committed' }),
    enabled: across, ...queryPolicies.pagedData,
  });
  const sourceList = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, enabled: across, ...queryPolicies.configuration });
  const leads = useInfiniteQuery({
    queryKey: [...leadImportKeys.leads(filter, sortKey, desc), pageSize, paged ? page : 0],
    // Only the first page of a view is counted; the count is capped (see fetchLeads).
    queryFn: ({ pageParam }) => fetchLeads(filter, sortKey, desc, size, pageParam, pageParam === (paged ? page * size : 0) ? 'capped' : 'none'),
    initialPageParam: paged ? page * size : 0,
    getNextPageParam: (last, pages) => {
      if (paged) return undefined;
      const loaded = pages.reduce((n, p) => n + p.rows.length, 0);
      if (pages[0].total_is_lower_bound) return last.rows.length < size ? undefined : loaded;
      return loaded < (pages[0].total ?? 0) ? loaded : undefined;
    },
    ...queryPolicies.pagedData,
  });
  const rows = useMemo(() => (leads.data?.pages ?? []).flatMap((p) => p.rows), [leads.data]);
  // The exact count, on request, for views over the capped count.
  const [exactFor, setExactFor] = useState<string | null>(null);
  const exact = useQuery({
    queryKey: leadImportKeys.leadCount(filter), queryFn: () => fetchLeadCount(filter),
    enabled: exactFor === filterKey, retry: false, ...queryPolicies.pagedData,
  });
  const first = leads.data?.pages[0];
  const total = exact.data ?? first?.total ?? 0;
  const totalIsLowerBound = exact.data == null && !!first?.total_is_lower_bound;
  const totalText = `${fmtInt(total)}${totalIsLowerBound ? '+' : ''}`;
  const selectedIds = useMemo(() => selected.map((r) => r.id), [selected]);
  const allMatching = allMatchingFor === filterKey && selected.length > 0;
  // Gmail-style: once every loaded row is checked, offer the rest of the matches.
  const offerAll = !allMatching && rows.length > 0 && selected.length === rows.length && (totalIsLowerBound || total > rows.length);
  const view: LeadsView = { filter, sortKey, desc, selected, selectedIds, total, totalIsLowerBound, allMatching, clearSelection };

  // Leads that can become merchants: not linked and nothing blocking them.
  const promotable = selected.filter((r) => !r.merchant && r.reasons.length === 0);
  const [confirm, confirmDialog] = useConfirm();
  const [zoom, setZoom] = useTableZoom('lead-import-leads', LEAD_IMPORT_ZOOM);
  const bulkPromote = useMutation({
    mutationFn: () => promoteBulk(promotable.map((r) => r.id)),
    onSuccess: (r) => {
      toast.success(`Created ${fmtInt(r.created)} merchants${r.skipped.length ? `, skipped ${r.skipped.length}` : ''}.`);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
      clearSelection();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const checks = showChecks ?? rows.some((r) => r.phone_verification_status || r.email_verification_status);
  const columns = useMemo<ColDef<BatchLead>[]>(() => {
    const cols: ColDef<BatchLead>[] = [
      {
        headerName: '', colId: 'bulk-selection', width: 46, maxWidth: 46, minWidth: 46,
        checkboxSelection: true, headerCheckboxSelection: true,
        sortable: false, filter: false, resizable: false, suppressMenu: true, pinned: 'left',
      },
      ...(across ? [] : [{ field: 'origin_row' as const, headerName: 'Row', width: 96, type: 'rightAligned', filter: false }]),
      { field: 'business_name', headerName: 'Business', flex: 1.4, minWidth: across ? 190 : 160 },
      { colId: 'name', headerName: 'Name', flex: 1, minWidth: 130, valueGetter: (p) => [p.data?.first_name, p.data?.last_name].filter(Boolean).join(' ') },
      { field: 'phone', headerName: 'Phone', width: 140 },
      { colId: 'phone_check', hide: !checks, headerName: 'Phone check', width: 140, sortable: false, valueGetter: (p) => p.data?.phone_verification_status ?? '',
        cellRenderer: (p: { data?: BatchLead }) => <VerifyCell status={p.data?.phone_verification_status} /> },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 150 },
      { colId: 'email_check', hide: !checks, headerName: 'Email check', width: 140, sortable: false, valueGetter: (p) => p.data?.email_verification_status ?? '',
        cellRenderer: (p: { data?: BatchLead }) => <VerifyCell status={p.data?.email_verification_status} /> },
      { field: 'state', headerName: 'State', width: 104 },
      {
        colId: 'status', headerName: 'Assignable', width: 170, sortable: false,
        valueGetter: (p) => (p.data?.eligible ? 'Assignable' : (p.data?.reasons ?? []).map((r) => REASON_LABEL[r] ?? r).join(', ')),
        cellRenderer: (p: { data?: BatchLead }) => p.data ? (p.data.eligible
          ? <Pill tone="success">Assignable</Pill>
          : <Pill tone="muted" title={p.data.merchant_label ?? undefined}>{(p.data.reasons ?? []).map((r) => REASON_LABEL[r] ?? r)[0] ?? 'No'}</Pill>) : null,
      },
      {
        colId: 'rep', headerName: 'Rep', width: 150, sortable: false,
        valueGetter: (p) => [p.data?.current_rep_name, p.data?.shared_with].filter(Boolean).join(', '),
        cellRenderer: (p: { data?: BatchLead }) => (
          <>
            {p.data?.current_rep_name ?? (p.data?.ever_assigned ? <span className="li-muted">Unassigned, had a rep</span> : <span className="li-muted">-</span>)}
            {p.data?.shared_with && <span className="li-subtag" title="Also given to these reps by a redistribution run">+ {p.data.shared_with}</span>}
          </>
        ),
      },
      {
        colId: 'repeat', headerName: 'Seen before', width: 120, sortable: false,
        valueGetter: (p) => p.data?.other_observations ?? 0,
        cellRenderer: (p: { data?: BatchLead }) => (p.data?.other_observations ?? 0) > 0
          ? <Pill tone="info">{p.data?.other_observations} other import{p.data?.other_observations === 1 ? '' : 's'}</Pill>
          : (p.data?.is_new_identity ? <Pill tone="success">New</Pill> : null),
      },
      ...(across ? [
        { colId: 'origin', headerName: 'Import', flex: 1, minWidth: 170, valueGetter: (p) => p.data?.file_name ?? '' },
        { colId: 'batch_date', headerName: 'Batch date', width: 120, valueGetter: (p) => fmtDay(p.data?.batch_date) },
        { colId: 'source', headerName: 'Source', width: 140, sortable: false, valueGetter: (p) => [p.data?.source, p.data?.sub_source].filter(Boolean).join(', ') },
      ] as ColDef<BatchLead>[] : []),
      { colId: 'merchant', headerName: 'Merchant', width: 160, sortable: false, valueGetter: (p) => p.data?.merchant_label ?? '' },
    ];
    return cols;
  }, [across, checks]);

  const count = (label: string, n: number | null | undefined, tone: 'neutral' | 'green' | 'amber' = 'neutral') => (
    n == null ? label : <>{label} <span className={`li-seg__count li-seg__count--${tone}`}>{fmtInt(n)}</span></>
  );
  const s = summary;
  const scoped = batches.length + sources.length > 0 || !!dateFrom || !!dateTo;
  const aside = renderAside?.(view);

  return (
    <div className={`li-leads${aside ? '' : ' li-leads--full'}`}>
      <div className="li-leads__main">
        {across && (
          <div className="li-scopebar" aria-label="Which imports">
            <div className="li-scopebar__pick">
              <MultiSelect id="li-scope-imports" uiLabel="imports" placeholder="All imports" value={batches}
                options={(committed.data ?? []).map((b) => ({ value: b.id, label: `${b.file_name ?? `Import ${b.id}`} (${fmtDay(b.batch_date)})` }))}
                onUpdate={(v) => setBatches(v.map(Number))} />
            </div>
            {sources.length > 0 && (
              <Segmented ariaLabel="Sources match" size="sm" value={sourcesExclude ? 'except' : 'only'} onChange={(v) => setSourcesExclude(v === 'except')}
                options={[{ value: 'only', label: 'Only' }, { value: 'except', label: 'All except' }]} />
            )}
            <div className="li-scopebar__pick li-scopebar__pick--narrow">
              <MultiSelect id="li-scope-sources" uiLabel="sources" placeholder="All sources" value={sources}
                options={(sourceList.data ?? []).map((x) => ({ value: x.id, label: x.source }))}
                onUpdate={(v) => setSources(v.map(Number))} />
            </div>
            <label className="li-scopebar__dates">
              <span>Batch date</span>
              <input type="date" className="bp5-input" aria-label="Batch date from" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
              <span aria-hidden>to</span>
              <input type="date" className="bp5-input" aria-label="Batch date to" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            {scoped && (
              <button type="button" className="li-btn li-btn--quiet" onClick={() => { setBatches([]); setSources([]); setSourcesExclude(false); setDateFrom(''); setDateTo(''); }}>
                Clear
              </button>
            )}
          </div>
        )}
        <div className="li-filterbar">
          <Segmented ariaLabel="Assignment filter" size="sm" value={assignment} onChange={setAssignment}
            options={[{ value: 'all', label: count('All', s?.total) }, { value: 'unassigned', label: count('Not assigned', s?.unassigned, 'amber') }, { value: 'never_assigned', label: count('Never assigned', s?.never_assigned, 'amber') }, { value: 'assigned', label: count('Assigned', s?.assigned, 'green') }]} />
          <Segmented ariaLabel="Seen before filter" size="sm" value={repeat} onChange={setRepeat}
            options={[{ value: 'all', label: 'Any' }, { value: 'repeats', label: count('Seen before', s?.repeats, 'amber') }, { value: 'first_seen', label: 'First time' }]} />
          <Segmented ariaLabel="Assignable filter" size="sm" value={eligibility} onChange={setEligibility}
            options={[{ value: 'all', label: 'Any' }, { value: 'eligible', label: count('Assignable', s?.eligible, 'green') }, { value: 'not_eligible', label: 'Not assignable' }]} />
          <form className="li-search" onSubmit={(e) => { e.preventDefault(); setAppliedSearch(search.trim()); }}>
            <InputGroup leftIcon="search" value={search} placeholder="Phone, name or email" onChange={(e) => setSearch(e.target.value)}
              rightElement={appliedSearch ? <Button minimal icon="cross" onClick={() => { setSearch(''); setAppliedSearch(''); }} aria-label="Clear search" /> : undefined} />
          </form>
        </div>
        <div className="li-grid-meta">
          <span>
            {leads.isFetching && !leads.isFetchingNextPage && rows.length > 0 ? 'Updating…' : `${totalText} leads`}
            {allMatching ? ', all selected' : selected.length ? `, ${fmtInt(selected.length)} checked` : ''}
            {totalIsLowerBound && !leads.isFetching && (
              exact.isFetching ? <span className="li-muted"> Counting…</span>
                : exact.isError ? <span className="li-muted" data-testid="li-count-failed"> Too many to count quickly. Narrow the filters to see the exact number.</span>
                : <> <button type="button" className="li-btn li-btn--link" onClick={() => setExactFor(filterKey)}>Count exactly</button></>
            )}
          </span>
          <span className="li-row li-wrap">
            {selected.length > 0 && <button type="button" className="li-btn li-btn--quiet" onClick={clearSelection}>Clear selection</button>}
            {renderActions?.(view)}
            <GridZoomControl zoom={zoom} onChange={setZoom} defaultZoom={LEAD_IMPORT_ZOOM} />
            {createMerchants && selected.length > 0 && (
              <button type="button" className="li-btn li-btn--secondary" disabled={promotable.length === 0 || bulkPromote.isPending}
                onClick={async () => {
                  const n = promotable.length;
                  const others = selected.length - n;
                  if (await confirm({
                    title: `Create ${fmtInt(n)} ${n === 1 ? 'merchant' : 'merchants'}?`,
                    body: <p>Each new merchant starts with status Lead and the lead source of its import. No main rep is set.{others > 0 ? ` ${fmtInt(others)} checked ${others === 1 ? 'lead is' : 'leads are'} already linked or blocked and ${others === 1 ? 'is' : 'are'} left out.` : ''}</p>,
                    action: n === 1 ? 'Create merchant' : `Create ${fmtInt(n)} merchants`,
                  })) bulkPromote.mutate();
                }}>
                Create merchants
              </button>
            )}
          </span>
        </div>
        {(offerAll || allMatching) && (
          <div className="li-selectall" role="status">
            {allMatching ? (
              <>All {totalText} matching leads are selected.{' '}
                <button type="button" className="li-btn li-btn--link" onClick={clearSelection}>Clear selection</button></>
            ) : (
              <>All {fmtInt(rows.length)} loaded leads are checked.{' '}
                <button type="button" className="li-btn li-btn--link" onClick={() => setAllMatchingFor(filterKey)}>Select all {totalText} matching leads</button></>
            )}
          </div>
        )}
        <ErrorNote error={leads.error} />
        <div className="li-leads__grid" style={{ ['--sky-grid-zoom' as string]: zoom }}>
        <WorkspaceGrid<BatchLead>
          rows={rows}
          columns={columns}
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => onOpenLead(r.id)}
          loading={leads.isLoading}
          emptyMessage="No leads match these filters."
          storageKey={across ? 'lead-import-all-leads' : 'lead-import-batch-leads'}
          height={height}
          rowHeight={Math.round(40 * zoom)}
          headerHeight={Math.round(34 * zoom)}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
          selectionResetKey={resetKey}
          onReachBottom={() => { if (leads.hasNextPage && !leads.isFetchingNextPage) leads.fetchNextPage(); }}
          sortModel={[sort]}
          onSortModelChange={(m) => setSort(m[0] ? { colId: m[0].colId, sort: m[0].sort } : defaultSort)}
        />
        </div>
        <div className="li-pager" aria-label="Pages">
          <span className="li-muted">Rows per page</span>
          <Segmented<PageSize> ariaLabel="Rows per page" size="sm" value={pageSize} onChange={setPageSize}
            options={[{ value: '50', label: '50' }, { value: '200', label: '200' }, { value: '500', label: '500' }, { value: 'all', label: 'All' }]} />
          {paged && (totalIsLowerBound || total > size) && (
            <span className="li-row">
              <Button small minimal icon="chevron-left" aria-label="Previous page" disabled={page === 0} onClick={() => setPage((p) => p - 1)} />
              <span className="li-muted">{fmtInt(page * size + 1)}-{fmtInt(page * size + rows.length)} of {totalText}</span>
              <Button small minimal icon="chevron-right" aria-label="Next page"
                disabled={totalIsLowerBound ? rows.length < size : (page + 1) * size >= total} onClick={() => setPage((p) => p + 1)} />
            </span>
          )}
        </div>
        {renderFooter?.(view)}
      </div>
      {aside && <aside className="li-leads__side">{aside}</aside>}
      {confirmDialog}
    </div>
  );
}
